import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import { v1 as uuidv1 } from 'uuid'
import { H6, Overline } from '@src/elements/typography/typography'
import { Card } from '../../card'
import { ExerciseInfoModal } from '@src/components/exercise-info/exercise-info'
import { ExerciseResponse } from '@src/data/exercise-response'
import { ExercisePreview } from '../exercise-preview/exercise-preview'
import { MenuButton } from '@src/elements/button/button'
import { Popover } from '@src/components/popover/popover'
import { OptionGroup } from '@src/elements/select/option-group'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Icon } from '@src/elements/icon/icon'
import { ExerciseCardPrint } from '@src/components/print/exercise-card-print'

import '../exercise-card.sass'
import SwitchButton from '@src/components/switch-button/switch-button'
import SessionManager from '@src/lib/session_manager'
import { exerciseName } from '@src/util/exercise-name'

@Component
export class ExerciseCardInfo extends Vue {

  @Prop({ required: true })
  exercise: ExerciseResponse

  @Prop({ required: false, default: 'default' })
  type: 'default' | 'noActions'

  @Prop({ required: false, default: false })
  showBreakpoint: boolean

  private showExerciseInfo: boolean = false

  private showPrintExercise: boolean = false
  private addTooltip = false

  constructor (props) {
    super(props)
  }

  created () {
    // after print, reset exercise to be printed
    const listeners = {
      afterPrint: () => this.resetPrint(),
      printChange: (mql) => {
        if (!mql.matches) {
          this.resetPrint()
        }
      },
      mediaQueryList: window.matchMedia('print')
    }
    window.addEventListener('afterprint', listeners.afterPrint)
    listeners.mediaQueryList.addListener(listeners.printChange)
    // removed again when the card goes, a print after that would reset a card that is gone
    this.$once('hook:beforeDestroy', () => {
      window.removeEventListener('afterprint', listeners.afterPrint)
      listeners.mediaQueryList.removeListener(listeners.printChange)
    })
  }

  updated () {
    this.checkTitle()
  }

  mounted () {
    this.checkTitle()
  }

  // check if title doesn't fit
  checkTitle () {
    let title = this.$refs.title as Vue
    this.addTooltip = title.$el.clientWidth < title.$el.scrollWidth
  }

  printExercise () {
    this.$el.classList.add('print-custom')
    this.showPrintExercise = true
  }

  resetPrint () {
    this.$el.classList.remove('print-custom')
    this.showPrintExercise = false
  }

  render (h: CreateElement) {
    return (
      <div class='card-wrapper'>
        <Card class='exercise-card exercise-card-info'>
          {this.renderHeader(h)}
          {this.renderPreview(h)}
          {this.renderTitle(h)}
          {this.renderSpecs(h)}
          {this.renderRight(h)}
        </Card>
          {this.renderExerciseBreakPoint(h)}
      </div>
    )
  }

  renderHeader (h: CreateElement) {
    return <Overline slot='header' class='header'>
      <Overline>
        {this.exercise.is2DTracked() && this.exercise.is3DTracked() ? this.$t('PAGE.CONTENT.WORKOUTS.TRACKED')
          : this.exercise.is2DTracked() ? this.$t('PAGE.CONTENT.EXERCISES.2D_TRACKED')
            : this.exercise.is3DTracked() ? this.$t('PAGE.CONTENT.EXERCISES.3D_TRACKED')
              : this.$t('PAGE.CONTENT.WORKOUTS.NON_TRACKED')} /&nbsp;</Overline>
      <Overline>ID {this.exercise.number}</Overline>
    </Overline>
  }

  renderPreview (h: CreateElement) {
    return <div slot='preview'>
      <ExercisePreview exercise={this.exercise} />
    </div>
  }

  renderTitle (h: CreateElement) {
    const title = exerciseName(this, this.exercise)
    return this.addTooltip ?
      <Tooltip slot='title' content={title}>
        <H6 class='title' ref='title'>{title}</H6>
      </Tooltip>
      : <H6 class='title' slot='title' ref='title'>{title}</H6>
  }

  renderSpecs (h: CreateElement) {
    const allEquipmentItems = this.exercise.equipment.map((equipmentName) => {
      return this.renderEquipment(h, equipmentName)
    })
    return <div slot='specs' class='specs'>
      {this.exercise.state === 'beta' ? <Overline class='label'>{this.$t('PAGE.CONTENT.EXERCISES.BETA')}</Overline> : null}
      {this.exercise.custom ? <Overline class='label'>{this.$t('PAGE.CONTENT.EXERCISES.CUSTOM')}</Overline> : null}
      <div class='equipmentWrapper'>{allEquipmentItems}</div>
    </div>
  }

  renderEquipment (h: CreateElement, equipment: string) {
    return <div key={uuidv1()} class='equipment'>
      <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipment.toUpperCase())}>
        <Icon name={'equipment/' + equipment} />
      </Tooltip>
    </div>
  }

  renderRight (h: CreateElement) {
    if (this.type === 'noActions') return null

    return <div slot='right' class='more-options'>
      <MenuButton>
        <Popover>
          <OptionGroup>
            <div class='view-exercise' onClick={this.showHideInfo}>{this.$t('VIEW')}</div>
            <div class='print' onClick={this.printExercise}>{this.$t('PRINT')}</div>
          </OptionGroup>
        </Popover>
      </MenuButton>
      {this.showExerciseInfo ?
        <ExerciseInfoModal exercise={this.exercise}
                           showModal={true}
                           closeCallback={this.showHideInfo} /> : null}
      {this.showPrintExercise ? <ExerciseCardPrint exercise={this.exercise} print={true}/> : ''}
      {SessionManager.Instance.notMemberOrAssistant() &&
        <Tooltip content={this.exercise.isFavorite
          ? this.$t('PAGE.CONTENT.EXERCISES.REMOVE_FROM_FAVORITES')
          : this.$t('PAGE.CONTENT.EXERCISES.ADD_TO_FAVORITES')}>
          <SwitchButton
            class={'favourites-button'}
            selected={this.favouriteExercise}
            unSelected={this.unfavouriteExercise}
            defaultSelected={this.exercise.isFavorite}
            selectedIcon={<Icon name={'util/starRed-filled'} />}
            unSelectedIcon={<Icon name={'util/starRed'} />}
          />
        </Tooltip>
      }
    </div>
  }

  favouriteExercise () {
    this.$store.dispatch('favouriteExercise', this.exercise)
    this.$emit('exercise-favourited', this.exercise)
  }
  unfavouriteExercise () {
    this.$store.dispatch('unfavouriteExercise', this.exercise)
    this.$emit('exercise-unfavourited', this.exercise)
  }

  renderExerciseBreakPoint (h: CreateElement) {
    if (!this.showBreakpoint) return ''

    return <div slot='specs' class='exercise-breakpoint'>
      <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.BREAKPOINT')}><hr/></Tooltip>
    </div>
  }

  showHideInfo () {
    this.showExerciseInfo = !this.showExerciseInfo
  }
}
